/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { Icon } from 'sarsaparilla';

import { FavoriteHeart } from 'shared-ui/favorites';

import type { SearchResultItem } from '../api/search/search.types';
import { CardImage } from './parts/CardImage';
import { CardInventoryTag } from './parts/CardInventoryTag';
import { CardLocation } from './parts/CardLocation';
import { CardFooter } from './parts/CardFooter';
import { CardLinkHeading } from './parts/CardLinkHeading';
import { useCardInteraction } from './hooks/useCardInteraction';

import styles from './SearchResultCard.module.scss';

export interface SearchResultCardProps extends React.HTMLAttributes<HTMLDivElement> {
    item: SearchResultItem;
    isActive?: boolean;
    hasCloseButton?: boolean;
    /** Whether to render the favorite/heart control. Defaults to `true`. */
    hasFavoriteHeart?: boolean;
    onCloseClick?: () => void;
    onHover?: (item: SearchResultItem) => void;
    onHoverLeave?: (item: SearchResultItem) => void;
    shouldFocusOnMount?: boolean;
}

export function SearchResultCard({
    item,
    isActive,
    hasCloseButton,
    hasFavoriteHeart = true,
    onCloseClick,
    onHover,
    onHoverLeave,
    className,
    shouldFocusOnMount,
    ...rest
}: SearchResultCardProps) {
    const headingId = React.useId();
    const { cardRef, onHoverOrFocus, onHoverLeaveOrBlur } = useCardInteraction({
        item,
        onHover,
        onHoverLeave,
        shouldFocusOnMount,
    });

    // Bail out if no item is provided
    if (!item) return null;

    return (
        <div
            ref={cardRef}
            className={styles.searchResultCard}
            onMouseEnter={onHoverOrFocus}
            onMouseLeave={onHoverLeaveOrBlur}
            onFocus={onHoverOrFocus}
            onBlur={onHoverLeaveOrBlur}
            tabIndex={shouldFocusOnMount ? -1 : undefined}
            role={shouldFocusOnMount ? 'dialog' : undefined}
            aria-labelledby={headingId}
            {...rest}
        >
            <div
                className={cx(styles.inner, {
                    [styles.isActive]: isActive,
                })}
            >
                <div className={styles.imageWrap}>
                    <CardImage item={item} className={styles.image} />

                    <div className={styles.imageButtonsGroup}>
                        <div className="left">{/* Badge here later */}</div>

                        <div className="right">
                            {hasFavoriteHeart ? (
                                <FavoriteHeart
                                    entityId={item.entity_id}
                                    entityType={item.entity_type}
                                    parentId={item.parent_id}
                                    parentType={item.parent_type}
                                    favAriaLabel={item.name}
                                    showFavoriteText={false}
                                    editable
                                />
                            ) : null}

                            {hasCloseButton ? (
                                <button
                                    type="button"
                                    className={styles.closeButton}
                                    onClick={onCloseClick}
                                    aria-label={`Close ${item.name} card`}
                                >
                                    <Icon iconName="close" className={styles.closeIcon} />
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className={styles.contentWrap}>
                    <div className={styles.topContent}>
                        <CardInventoryTag item={item} hasText />

                        <div>
                            <CardLinkHeading
                                item={item}
                                id={headingId}
                                className={styles.heading}
                                gaTrackingId="858265557121"
                            />

                            <CardLocation
                                parent={item.parent_name}
                                distance={item.distance}
                                city={item.city}
                                state={item.state_code}
                            />
                        </div>
                    </div>

                    <CardFooter item={item} />
                </div>
            </div>
        </div>
    );
}
